import { GraphQLError } from "graphql";
import { ClientError } from "graphql-request";

import { APIError } from "lib/Api";

const TIMEOUT_STATUS_CODES = new Set([408, 504]);

/**
 * True for a request timeout across every surface we can observe one:
 *  - the hand-written REST layer (`APIError`) or an upstream gateway
 *    (`ClientError`) returning HTTP 504;
 *  - an app-service GraphQL timeout — a `ClientError` whose GraphQL error
 *    carries `extensions.statusCode` 408/504 (e.g. the spend resolver's
 *    composer-deadline error);
 *  - a client-side `AbortSignal.timeout(...)` firing, which rejects fetch with
 *    a `TimeoutError` (or `AbortError` for a manual abort).
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof APIError) {
    return TIMEOUT_STATUS_CODES.has(error.statusCode);
  }
  if (error instanceof ClientError) {
    const httpStatus = error.response?.status;
    if (typeof httpStatus === "number" && TIMEOUT_STATUS_CODES.has(httpStatus)) {
      return true;
    }
    return Boolean(
      error.response?.errors?.some((e) => {
        const status = e.extensions?.statusCode;
        return typeof status === "number" && TIMEOUT_STATUS_CODES.has(status);
      }),
    );
  }
  if (error instanceof Error) {
    return error.name === "TimeoutError" || error.name === "AbortError";
  }
  return false;
}

// app-service nests the transport status under `extensions.originalError` for auth failures.
function hasUnauthorizedOriginalError(extensions: GraphQLError["extensions"]): boolean {
  const originalError = extensions?.originalError;
  return (
    typeof originalError === "object" &&
    originalError !== null &&
    "statusCode" in originalError &&
    originalError.statusCode === 401
  );
}

/**
 * True when the app token was rejected — a GraphQL `UNAUTHENTICATED` error or a plain HTTP 401.
 */
export function isAuthenticationError(error: unknown): boolean {
  if (error instanceof APIError) {
    return error.statusCode === 401;
  }
  if (error instanceof ClientError) {
    if (error.response?.status === 401) {
      return true;
    }
    return Boolean(
      error.response?.errors?.some(
        (e) =>
          e.extensions?.code === "UNAUTHENTICATED" || hasUnauthorizedOriginalError(e.extensions),
      ),
    );
  }
  return false;
}

/** One retry is enough to pick up a token that has since been refreshed; more just spams 401s. */
const AUTH_ERROR_MAX_RETRIES = 1;

/**
 * Shared react-query retry policy: never retry a timeout (surface it cleanly instead of
 * hammering a slow backend), retry an authentication failure once, otherwise retry up to 3×.
 *
 * The single auth retry matters because the client resolves its authorization header per request:
 * a token that expired mid-flight (a sleeping laptop, an unfocused tab) will have been refreshed
 * by the time the retry goes out, so it succeeds without the user seeing anything.
 */
export function retryUnlessUnrecoverable(failureCount: number, error: unknown): boolean {
  if (isTimeoutError(error)) {
    return false;
  }
  if (isAuthenticationError(error)) {
    return failureCount < AUTH_ERROR_MAX_RETRIES;
  }
  return failureCount < 3;
}
